import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BoxesIcon, MoreHorizontalIcon, PlusIcon, SearchIcon, XCircleIcon,
} from 'lucide-react';

import { api, type ModuleRow } from '@/lib/api';
import { useContent, useLabel, useT, useTn, type Translate } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { useUrlState } from '@/lib/url-state';
import { useSearchShortcut } from '@/lib/shortcuts';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog } from '@/components/ui/dialog';
import { CreateModuleWizard } from '@/features/modules/create-wizard';
import { useCannotBecause } from '@/lib/permissions';

/**
 * Modules — the catalogue's design surface (build/09 §2, §5; `plan-beta` T5/S2).
 *
 * WHAT THIS SCREEN NOW DOES THAT IT COULD NOT. A module could be created only
 * through the API and could be switched off only *permanently*: `draft` was
 * reachable at create and never returned to, so an operator wanting to pull a
 * module out of new plans for a fortnight had one button, and it was `retire`.
 *
 * The three transitions are now here, and the difference between them is the
 * whole point:
 *
 *   - **Publish** — draft → active. Answers with the coverage it is publishing
 *     into, because a module published into an amber matrix generates a short
 *     instrument or nothing at all.
 *   - **Unpublish** — active → draft. The reversible switch-off. Reports the
 *     persona plans it breaks and proceeds.
 *   - **Retire** — permanent. Refused while a persona plan still names the
 *     module, and the refusal points at unpublish.
 *
 * All three stop the NEXT candidate and leave open runs untouched: an instance
 * froze its items and pinned its config, so `status` gates generation and
 * nothing else. That is why unpublish is safe enough to be one click.
 */

const STATUS_TONE: Record<string, 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  draft: 'secondary',
  retired: 'outline',
};

function LifecycleMenu({ module }: { module: ModuleRow }) {
  const queryClient = useQueryClient();
  const t = useT();
  const tn = useTn();
  const label = useLabel();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['modules'] });
    void queryClient.invalidateQueries({ queryKey: ['module', module.moduleId] });
    // Switching a module off changes which cells can be filled.
    void queryClient.invalidateQueries({ queryKey: ['coverage'] });
  };

  const publish = useMutation({
    mutationFn: () => api.publishModule(module.moduleId),
    onSuccess: ({ readiness }) => {
      const amber = readiness.coverage.filter((c) => c.status === 'warning').length;
      if (amber > 0) {
        // Published, and honestly: the operator needs to know the bank cannot
        // fill what the matrix asks for BEFORE a candidate meets it.
        notify.info(
          tn('modules.published.amber', amber, { module: module.moduleId }),
          t('modules.published.amber.detail'),
        );
      } else {
        notify.success(
          t('modules.published', { module: module.moduleId }),
          t('modules.published.detail'),
        );
      }
      refresh();
    },
    onError: (err) =>
      notify.error(t('modules.publishFailed'), err instanceof Error ? err.message : undefined),
  });

  const unpublish = useMutation({
    mutationFn: () => api.unpublishModule(module.moduleId),
    onSuccess: ({ impact }) => {
      notify.success(
        t('modules.unpublished', { module: module.moduleId }),
        impact.personaPlans.length > 0
          ? t('modules.unpublished.breaking', {
              plans: impact.personaPlans.length,
              names: impact.personaPlans.join(', '),
              open: impact.openInstances,
            })
          : t('modules.unpublished.clean', { open: impact.openInstances }),
      );
      refresh();
    },
    onError: (err) =>
      notify.error(t('modules.unpublishFailed'), err instanceof Error ? err.message : undefined),
  });

  const retire = useMutation({
    mutationFn: () => api.retireModule(module.moduleId),
    onSuccess: () => {
      notify.success(
        t('modules.retired', { module: module.moduleId }),
        t('modules.retired.detail'),
      );
      refresh();
    },
    // MODULE_IN_USE is the interesting failure, and `ApiError` already carries
    // the server's message — which names the blocking plans and points at
    // unpublish. Surfacing it verbatim keeps one wording of the rule rather
    // than a second, shorter one that omits the way out.
    onError: (err) =>
      notify.error(t('modules.retireFailed'), err instanceof Error ? err.message : undefined),
  });

  const busy = publish.isPending || unpublish.isPending || retire.isPending;
  /**
   * R3. All three transitions are `catalogue:publish` — admin only, and the
   * line that separates an admin from a psychometrician. A menu of items that
   * all fail is worse than a disabled trigger that says why, so the refusal is
   * on the trigger and the menu never opens.
   */
  const cannotPublish = useCannotBecause('catalogue:publish');

  const retired = module.status === 'retired';
  const switchId = `module-active-${module.moduleId}`;
  /**
   * WHY THE REVERSIBLE HALF IS A SWITCH AND THE TERMINAL HALF IS NOT.
   *
   * Publish and unpublish are one question — *is this module offered to
   * candidates* — asked in two directions, and a menu made an operator open a
   * list to answer it while the answer was already drawn beside the list as a
   * badge. So the badge became the label of a switch: the state you were reading
   * is the control you press, and its direction is visible without a click.
   *
   * **Retire stays in the menu, and it stays alone there.** It is terminal, it
   * cannot be expressed by a two-state control at all, and a control that can be
   * flicked is the wrong shape for a decision that cannot be flicked back. A
   * retired module's switch is off and disabled, saying the same thing.
   *
   * There is no confirm step on the switch, deliberately: unpublish is reversible
   * by the same gesture, and the toast reports what it broke — which plans, how
   * many open runs — so the consequence is *read* rather than *predicted*. A
   * confirm dialog in front of a reversible act mostly trains people to dismiss
   * dialogs.
   */
  return (
    <>
      <Label
        htmlFor={switchId}
        className={cn('flex items-center gap-2', !retired && cannotPublish === null && 'cursor-pointer')}
        title={cannotPublish ?? (retired ? t('modules.action.terminal') : undefined)}
      >
        <Switch
          id={switchId}
          checked={module.status === 'active'}
          disabled={busy || retired || cannotPublish !== null}
          // The visible label is the status badge, which names a state rather
          // than an action, so the switch carries the action AND the identity —
          // nine cards on this screen and "Active, on" would name none of them.
          aria-label={t('modules.toggleActive', { module: module.moduleId })}
          onCheckedChange={(next) => (next ? publish.mutate() : unpublish.mutate())}
        />
        <Badge variant={STATUS_TONE[module.status] ?? 'outline'}>
          {label('moduleStatus', module.status)}
        </Badge>
      </Label>

      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={busy || cannotPublish !== null}
          title={cannotPublish ?? undefined}
          aria-label={t('modules.lifecycleActions', { module: module.moduleId })}
        >
          <MoreHorizontalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="font-normal">
          <span className="block text-xs font-medium">
            {t('modules.currently', { status: label('moduleStatus', module.status) })}
          </span>
          <span className="text-muted-foreground block text-xs">
            {label('modules.statusHint', module.status)}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Publish and unpublish are the switch's job now. Leaving copies here
            would give one act two controls whose states could disagree on
            screen, which is the bug the switch was added to remove. */}
        {!retired && (
          <DropdownMenuItem variant="destructive" onSelect={() => retire.mutate()}>
            {t('modules.action.retire')}
          </DropdownMenuItem>
        )}

        {retired && <DropdownMenuItem disabled>{t('modules.action.terminal')}</DropdownMenuItem>}
      </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export function Modules() {
  const [creating, setCreating] = useState(false);
  const t = useT();
  const tn = useTn();
  const content = useContent();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  /**
   * R3. Creating a module is `design:draft` — held by a psychometrician,
   * because a draft is reversible. PUBLISHING it is not; that is
   * `catalogue:publish` and it is gated separately in `LifecycleMenu`.
   *
   * Editing a scoring config carries the same capability and is now gated on
   * the Scoring screen, where the control moved (J1).
   */
  const cannotDraft = useCannotBecause('design:draft');

  /**
   * The filter lives in the URL, like every other list in the console.
   *
   * It arrived with X2: the topbar search offers a "see all modules matching…"
   * row, and a link that carried a term this screen could not read would have
   * been the same broken promise one level down. `GET /api/modules` has no text
   * parameter and the screen already holds the whole catalogue, so the match is
   * made here — on the id and on BOTH halves of the bilingual name, so a module
   * findable in Arabic is findable from an English console.
   */
  const [{ q: search }, setFilters] = useUrlState({ q: '' });
  const searchBox = useRef<HTMLInputElement>(null);
  useSearchShortcut(searchBox);

  const all = useMemo(() => data?.items ?? [], [data]);
  const modules = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (m) =>
        m.moduleId.toLowerCase().includes(needle)
        || m.name.en.toLowerCase().includes(needle)
        || m.name.ar.toLowerCase().includes(needle),
    );
  }, [all, search]);

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('modules.title')}
        description={t('modules.description')}
        actions={
          <div className="flex items-center gap-2">
            {data && <Badge variant="secondary">{tn('modules.count', data.total)}</Badge>}
            <Button size="sm" onClick={() => setCreating(true)}
              disabled={cannotDraft !== null} title={cannotDraft ?? undefined}>
              <PlusIcon className="size-4" />
              {t('modules.new')}
            </Button>
          </div>
        }
      />

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      )}

      {error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('modules.loadFailed')}
          description={error instanceof Error ? error.message : t('table.errorDescription')}
          actions={<Button onClick={() => void refetch()}>{t('common.retry')}</Button>}
        />
      )}

      {data && all.length === 0 && (
        <EmptyState
          icon={BoxesIcon}
          title={t('modules.empty.title')}
          description={t('modules.empty.description')}
          actions={
            <Button onClick={() => setCreating(true)}
              disabled={cannotDraft !== null} title={cannotDraft ?? undefined}>
              {t('modules.empty.action')}
            </Button>
          }
        />
      )}

      {all.length > 0 && (
        <>
          <div className="relative mb-4">
            <SearchIcon className="text-muted-foreground pointer-events-none absolute inset-inline-start-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={searchBox}
              value={search}
              onChange={(e) => setFilters({ q: e.target.value })}
              placeholder={t('modules.searchPlaceholder')}
              aria-label={t('modules.searchPlaceholder')}
              className="ps-9"
            />
          </div>

          {/* "Nothing matches" and "there is no catalogue" are different facts,
              and only one of them is the operator's cue to create a module. */}
          {modules.length === 0 ? (
            <EmptyState
              icon={SearchIcon}
              title={t('modules.noMatches')}
              description={t('modules.noMatches.description', { query: search })}
              actions={
                <Button variant="outline" onClick={() => setFilters({ q: '' })}>
                  {t('questions.empty.clearFilters')}
                </Button>
              }
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {modules.map((m: ModuleRow) => (
                <ModuleCard key={m.moduleId} module={m} t={t} content={content} />
              ))}
            </div>
          )}
        </>
      )}

      {/* The scoring-config dialog used to open from here. It lives on Scoring
          now (J1) — `09 §Screen 5` always specified it there, and this screen
          links across rather than owning a second copy. */}
      <Dialog open={creating} onOpenChange={setCreating}>
        {creating && <CreateModuleWizard onClose={() => setCreating(false)} />}
      </Dialog>
    </div>
  );
}

/**
 * ONE LANGUAGE — the operator's (2026-08-02).
 *
 * This card used to print the other language underneath the name. The rule now
 * is that a **view** renders the console's language and only an **editor**
 * shows the pair, because an author comparing translations is doing a different
 * job from an operator scanning a catalogue, and only the first one needs both
 * on screen at once.
 */
function ModuleCard({
  module: m, t, content,
}: {
  module: ModuleRow;
  t: Translate;
  content: (text: { en: string; ar: string } | undefined) => string;
}) {
  return (
            <Card key={m.moduleId}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{content(m.name)}</CardTitle>
                  <div className="flex shrink-0 items-center gap-1">
                    {/* The badge is the switch's label, so the status a reader
                        is already looking at is also the thing they click. */}
                    <LifecycleMenu module={m} />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="font-mono">
                    {m.modelType}
                  </Badge>
                  <Badge variant="outline">
                    {t('modules.configVersion', { version: m.scoringConfigVersion })}
                  </Badge>
                  {/* The contract carries BOTH versions and they mean different
                      things — 09 §2 asks for the plugin version alongside. */}
                  <Badge variant="outline">
                    {t('modules.pluginVersion', { version: m.pluginVersion })}
                  </Badge>
                </div>
                <dl className="text-muted-foreground grid grid-cols-2 gap-y-1">
                  <dt>{t('modules.itemBank')}</dt>
                  <dd className="text-foreground">
                    {t('modules.certifiedOf', {
                      certified: m.certifiedCount,
                      total: m.questionCount,
                    })}
                  </dd>
                  <dt>{t('modules.duration')}</dt>
                  <dd className="text-foreground">
                    {t('common.minutes', { minutes: m.estimatedMinutes })}
                  </dd>
                </dl>
                {/**
                  * J4 — a LINK to Scoring, not a dialog opened from here.
                  *
                  * The editor moved to the screen `09 §Screen 5` always
                  * specified for it; this card keeps the path from where the
                  * work already is, filtered to this module. One screen owns
                  * the editing and both routes reach it.
                  *
                  * Ungated, like the item-analysis link beside it: navigating
                  * to a screen is a read. The Configure button *there* carries
                  * the `design:draft` gate, which is where the write is.
                  */}
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <Link to={`/scoring?moduleId=${encodeURIComponent(m.moduleId)}`}>
                    {t('modules.scoringConfig')}
                  </Link>
                </Button>
                {/**
                  * M1, reachable from where the work already is.
                  *
                  * Ungated, like the route: an item analysis is a read, it names
                  * no candidate, and `plan-preproduction` §2 is explicit that
                  * viewing analytics is not modelled as a capability — it is what
                  * signing in already means.
                  */}
                <Button variant="ghost" size="sm" className="w-full" asChild>
                  <Link to={`/item-analysis?moduleId=${encodeURIComponent(m.moduleId)}`}>
                    {t('itemAnalysis.title')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
  );
}
